/**
 * AddActivityModal — controlled picker → form modal.
 *
 * Lifted out of `AssetActivitiesStep`'s admin-mode flow so the new
 * multi-add Entry step can reuse the same picker → form UX without
 * persisting on save (rows stay in the parent's in-memory state until
 * the Entry-step Submit). Open state is fully controlled by the parent
 * — no asset coupling, no inline trigger button.
 *
 * Behaviour mirrors the asset-detail page modal:
 *   - Screen 1: category list (reuses `OTHER_CATEGORIES` for non-asset use)
 *   - Screen 2: `ActivityFields` form pre-filled from the picked suggestion
 *   - On save, calls `onSave(entry)` with the completed `AssetActivityEntry`
 *     and slides back to the picker so users can immediately add another.
 */

import { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  type ActivityUnit,
  type FuelType,
  type ActivityTypeWithUnits,
  getValidUnits,
} from '../../../types/emissions';
import { isActivityFormComplete, type AssetActivityEntry } from '../types';
import { ActivityFields } from '../ActivityFields';
import {
  createEmptyActivity,
  OTHER_CATEGORIES,
  type ActivityCategory,
  type SuggestedActivity,
} from '../AssetActivitiesStep';
import { ActivityCategoryPicker } from './ActivityCategoryPicker';

export interface AddActivityModalProps {
  isOpen: boolean;
  /** Categories to show on screen 1. Defaults to `OTHER_CATEGORIES` for non-asset use. */
  categories?: ActivityCategory[];
  /** Defaults applied when creating a new entry (region, period). */
  defaults: {
    region: string;
    periodStart: string;
    periodEnd: string;
  };
  /**
   * Extra defaults applied to every newly-picked entry on top of the
   * suggestion's own defaults. Used by the asset wizard to pre-populate
   * (and lock) `vehicleType` from the asset itself for Vehicle assets.
   */
  extraEntryDefaults?: Partial<AssetActivityEntry>;
  /** Field keys to lock on the form view in addition to the suggestion's own locked keys. */
  extraLockedFields?: string[];
  /**
   * When set, the modal opens directly on the form view, prefilled with this
   * entry. The picker is skipped (user can't change activity type from edit
   * mode — they'd delete and re-add). Header reads "Edit — <label>".
   * Set to `null` (or undefined) for normal "add" mode.
   */
  editing?: { entry: AssetActivityEntry; label: string } | null;
  /** Called when the user clicks the primary action on the form view. */
  onSave: (entry: AssetActivityEntry) => void;
  onClose: () => void;
}

interface ModalFormState {
  activity: AssetActivityEntry | null;
  label: string;
  lockedFields: Set<string>;
}

const EMPTY_FORM: ModalFormState = { activity: null, label: '', lockedFields: new Set() };

export const AddActivityModal = ({
  isOpen,
  categories = OTHER_CATEGORIES,
  defaults,
  extraEntryDefaults,
  extraLockedFields,
  editing,
  onSave,
  onClose,
}: AddActivityModalProps) => {
  const [view, setView] = useState<'picker' | 'form'>('picker');
  const [form, setForm] = useState<ModalFormState>(EMPTY_FORM);

  // Edit mode: when `editing` is set on open, jump straight to the form
  // view prefilled with that entry. Reset to picker on close.
  //
  // Periods get sliced to YYYY-MM so the `MonthPicker` renders them —
  // bulk-imported rows arrive in YYYY-MM-DD which the picker would
  // otherwise drop. The expand-back-to-full-date happens at submit time
  // (see ActivityFormFlow's expandMonthToPeriodRange usage).
  useEffect(() => {
    if (isOpen && editing) {
      setForm({
        activity: {
          ...editing.entry,
          periodStart: editing.entry.periodStart ? editing.entry.periodStart.slice(0, 7) : '',
          periodEnd: editing.entry.periodEnd ? editing.entry.periodEnd.slice(0, 7) : '',
        },
        label: editing.label,
        lockedFields: new Set(),
      });
      setView('form');
    } else if (!isOpen) {
      setForm(EMPTY_FORM);
      setView('picker');
    }
  }, [isOpen, editing]);

  const reset = () => {
    setView('picker');
    setForm(EMPTY_FORM);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const openForm = (suggested: SuggestedActivity) => {
    const entry = createEmptyActivity({
      sourceLabel: suggested.label,
      scope: suggested.scope,
      activityType: suggested.activityType,
      region: defaults.region,
      periodStart: defaults.periodStart,
      periodEnd: defaults.periodEnd,
      // Leave Name/Label blank by default — user opts in. The suggestion's
      // label is already shown in the modal header and stored as
      // `sourceLabel` for the table's display fallback.
      name: '',
    });
    const locked = new Set<string>();
    if (suggested.defaults) {
      Object.assign(entry, suggested.defaults);
      for (const key of Object.keys(suggested.defaults)) {
        locked.add(key);
      }
    }
    if (extraEntryDefaults) {
      Object.assign(entry, extraEntryDefaults);
    }
    if (extraLockedFields) {
      for (const key of extraLockedFields) {
        locked.add(key);
      }
    }
    setForm({ activity: entry, label: suggested.label, lockedFields: locked });
    setView('form');
  };

  const updateForm = (updates: Partial<AssetActivityEntry>) => {
    if (!form.activity) {
      return;
    }
    const merged = { ...form.activity, ...updates };
    // Mirror AssetActivitiesStep: reset unit when fuelType changes if unit becomes invalid.
    if ('fuelType' in updates && updates.fuelType !== form.activity.fuelType) {
      const validUnits = getValidUnits(
        form.activity.activityType as ActivityTypeWithUnits,
        (updates.fuelType as FuelType) || undefined,
      );
      if (validUnits.length > 0 && form.activity.unit && !validUnits.includes(form.activity.unit as ActivityUnit)) {
        merged.unit = '' as ActivityUnit;
      }
    }
    setForm((prev) => ({ ...prev, activity: merged }));
  };

  const handleSave = () => {
    if (!form.activity) {
      return;
    }
    onSave(form.activity);
    // Close after save — both add-mode and edit-mode. The user clicks
    // "+ Add Activity" again from the toolbar when they want another row.
    // Reset the internal view so a future open lands on the picker
    // (handleClose's reset handles the form state).
    onClose();
  };

  const canSave = form.activity ? isActivityFormComplete(form.activity) : false;

  const categorySections = <ActivityCategoryPicker categories={categories} onSelect={openForm} />;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size='lg'>
      <ModalHeader toggle={handleClose}>
        {view === 'form' ? `${editing ? 'Edit' : 'Add'} — ${form.label}` : 'Add Activity'}
      </ModalHeader>
      <ModalBody style={{ overflow: 'hidden', padding: 0 }}>
        <div
          style={{
            display: 'flex',
            transition: 'transform 250ms ease-in-out',
            transform: view === 'form' ? 'translateX(-50%)' : 'translateX(0)',
            width: '200%',
          }}
        >
          <div
            className='tw:p-4 tw:overflow-hidden'
            style={{ width: '50%', flexShrink: 0, boxSizing: 'border-box' }}
          >
            {categorySections}
          </div>
          <div
            className='tw:p-4 tw:overflow-hidden'
            style={{ width: '50%', flexShrink: 0, boxSizing: 'border-box' }}
          >
            {form.activity && (
              <ActivityFields
                values={form.activity}
                onUpdate={(updates) => updateForm(updates as Partial<AssetActivityEntry>)}
                defaultDetailsOpen={false}
                lockedFields={form.lockedFields}
              />
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter style={{ minHeight: '4rem' }}>
        {view === 'form' && (
          <>
            {!editing && (
              <Button variant='secondary' onClick={() => { setView('picker'); setForm(EMPTY_FORM); }}>
                Back
              </Button>
            )}
            <Button
              className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
              onClick={handleSave}
              disabled={!canSave}
            >
              {editing ? 'Save Changes' : 'Add Activity'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};
